export const MATCH_HISTORY_STORAGE_KEY = 'riftbound:match-history:v1';
export const MATCH_HISTORY_SCHEMA_VERSION = 1;
const MAX_MATCH_HISTORY = 100;

function normalizeMatch(match) {
  if (!match || typeof match !== 'object') return null;
  if (match.result !== 'win' && match.result !== 'loss') return null;
  const completedAt = new Date(match.completedAt);
  if (!match.id || Number.isNaN(completedAt.getTime())) return null;

  return {
    id: String(match.id),
    completedAt: completedAt.toISOString(),
    result: match.result,
    opponent: typeof match.opponent === 'string' && match.opponent.trim()
      ? match.opponent.trim()
      : 'Opponent',
    playerScore: Number.isFinite(Number(match.playerScore)) ? Number(match.playerScore) : 0,
    opponentScore: Number.isFinite(Number(match.opponentScore)) ? Number(match.opponentScore) : 0,
    reason: typeof match.reason === 'string' ? match.reason : '',
    mode: typeof match.mode === 'string' ? match.mode : 'unknown',
    deckName: typeof match.deckName === 'string' ? match.deckName : '',
  };
}

export function parseMatchHistory(rawValue) {
  if (!rawValue) return [];
  try {
    const parsed = JSON.parse(rawValue);
    if (
      parsed?.schemaVersion !== MATCH_HISTORY_SCHEMA_VERSION
      || !Array.isArray(parsed.matches)
    ) {
      return [];
    }
    return parsed.matches
      .map(normalizeMatch)
      .filter(Boolean)
      .sort((matchA, matchB) => Date.parse(matchB.completedAt) - Date.parse(matchA.completedAt))
      .slice(0, MAX_MATCH_HISTORY);
  } catch {
    return [];
  }
}

export function loadMatchHistory(storage = globalThis.localStorage) {
  try {
    return parseMatchHistory(storage?.getItem(MATCH_HISTORY_STORAGE_KEY));
  } catch {
    return [];
  }
}

export function recordCompletedMatch(match, storage = globalThis.localStorage) {
  const normalized = normalizeMatch(match);
  if (!normalized || !storage) return false;

  try {
    const existing = loadMatchHistory(storage);
    const withoutDuplicate = existing.filter((entry) => entry.id !== normalized.id);
    storage.setItem(MATCH_HISTORY_STORAGE_KEY, JSON.stringify({
      schemaVersion: MATCH_HISTORY_SCHEMA_VERSION,
      matches: [normalized, ...withoutDuplicate].slice(0, MAX_MATCH_HISTORY),
    }));
    return true;
  } catch {
    return false;
  }
}

export function matchFromGameResult(gameResult, context) {
  if (!gameResult?.gameOver || !context?.id) return null;
  const finalScores = Array.isArray(gameResult.finalScores) ? gameResult.finalScores : [];
  const scoreFor = (playerId) => (
    finalScores.find((score) => Number(score.playerId) === playerId)?.points || 0
  );
  return normalizeMatch({
    id: context.id,
    completedAt: context.completedAt,
    result: Number(gameResult.winnerId) === 0 ? 'win' : 'loss',
    opponent: context.opponent || 'Bot',
    playerScore: scoreFor(0),
    opponentScore: scoreFor(1),
    reason: gameResult.reason || '',
    mode: context.mode || 'bot',
    deckName: context.deckName || '',
  });
}

export function summarizeMatchHistory(matches) {
  const validMatches = Array.isArray(matches)
    ? matches.filter((match) => match?.result === 'win' || match?.result === 'loss')
    : [];
  const wins = validMatches.reduce(
    (total, match) => total + (match.result === 'win' ? 1 : 0),
    0
  );
  const losses = validMatches.length - wins;
  return {
    totalGames: validMatches.length,
    wins,
    losses,
    winRate: validMatches.length > 0 ? Math.round((wins / validMatches.length) * 100) : 0,
  };
}
